import { useEffect } from 'react'

type PagedQuery = {
  hasNextPage: boolean
  isFetching: boolean
  isFetchNextPageError: boolean
  fetchNextPage: () => unknown
}

/**
 * Loads the following pages of a paginated list by themselves, until all of it
 * is there. For lists that are grouped or split into tabs on the device, where
 * a "load more on scroll" would fill the sections in a confusing order.
 * Stops after a page that failed: no endless retry while the server is down.
 *
 * It waits for any request of the list to end, not only for a next page: a
 * refresh started at the same moment (a screen that refreshes when it comes
 * in front, a photo just added) replaces the next page being asked, and the
 * end of that refresh is then the only sign that the next page is still due.
 *
 * `enabled` false leaves the list at the pages it has: for a caller that only
 * needs the rest in some cases.
 */
export const useLoadAllPages = (
  { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage }: PagedQuery,
  enabled = true,
) => {
  useEffect(() => {
    if (enabled && hasNextPage && !isFetching && !isFetchNextPageError) void fetchNextPage()
  }, [enabled, hasNextPage, isFetching, isFetchNextPageError, fetchNextPage])
}
